import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Box, Chip, Tabs, Tab, Alert, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material'
import { Add, Warning, Edit, Delete, Science, Build, Inventory2, FilterList, Clear } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal, PermissionGate, ExportButtons } from '@components/common'
import { InventoryForm } from '@components/inventory'
import { usePermissions } from '@context/PermissionContext'
import { toast } from 'react-toastify'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const INVENTORY_TYPES = [
  { value: '', label: 'All Items', icon: <Inventory2 /> },
  { value: 'spare_part', label: 'Spare Parts', icon: <Build /> },
  { value: 'reagent', label: 'Reagents', icon: <Science /> },
  { value: 'consumable', label: 'Consumables', icon: <Inventory2 /> },
  { value: 'accessory', label: 'Accessories', icon: <Inventory2 /> },
]

const columns = [
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'category_name', headerName: 'Category', sortable: true, renderCell: (v) => v || '-' },
  { field: 'part_number', headerName: 'Part #', sortable: true, width: 100 },
  {
    field: 'inventory_type',
    headerName: 'Type',
    renderCell: (v) => (
      <Chip
        label={INVENTORY_TYPES.find((t) => t.value === v)?.label || v || 'Spare Part'}
        size="small"
        variant="outlined"
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
  { field: 'manufacturer', headerName: 'Manufacturer', sortable: true, renderCell: (v) => v || '-' },
  { field: 'department_name', headerName: 'Section/Unit', sortable: true, renderCell: (v) => v || '-' },
  { field: 'location', headerName: 'Location', sortable: true, renderCell: (v) => v || '-' },
  {
    field: 'current_stock',
    headerName: 'In Stock',
    sortable: true,
    align: 'right',
  },
  { field: 'reorder_point', headerName: 'Reorder Pt', sortable: true, align: 'right', width: 90 },
  {
    field: 'stock_status',
    headerName: 'Status',
    renderCell: (_, row) => {
      const isLow = row.is_low_stock || (row.current_stock <= row.reorder_point)
      return (
        <Chip
          label={isLow ? 'Low Stock' : 'In Stock'}
          size="small"
          color={isLow ? 'error' : 'success'}
          icon={isLow ? <Warning fontSize="small" /> : undefined}
        />
      )
    },
  },
  {
    field: 'unit_cost',
    headerName: 'Unit Cost',
    sortable: true,
    align: 'right',
    renderCell: (value) => value ? `UGX ${Number(value).toLocaleString()}` : '-',
  },
  {
    field: 'expiry_date',
    headerName: 'Expiry',
    renderCell: (v) => {
      if (!v) return '-'
      const date = new Date(v)
      const today = new Date()
      const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24))
      const isExpired = daysUntil < 0
      const isExpiringSoon = daysUntil >= 0 && daysUntil <= 30
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          {formatDate(date)}
          {isExpired && <Chip label="Expired" size="small" color="error" />}
          {isExpiringSoon && !isExpired && <Chip label={`${daysUntil}d`} size="small" color="warning" />}
        </Box>
      )
    },
  },
]

const InventoryList = () => {
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [typeFilter, setTypeFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const { canEdit, canDelete } = usePermissions()

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Additional filters
  const [filters, setFilters] = useState({
    category: '',
    department: '',
    low_stock: '',
  })

  // Filter options
  const [categories, setCategories] = useState([])
  const [departments, setDepartments] = useState([])

  // Refs for stable callbacks
  const filtersRef = useRef(filters)
  const typeFilterRef = useRef(typeFilter)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'name', headerName: 'Name' },
    { field: 'category_name', headerName: 'Category' },
    { field: 'part_number', headerName: 'Part #' },
    { field: 'inventory_type', headerName: 'Type', renderValue: (v) => INVENTORY_TYPES.find(t => t.value === v)?.label || v },
    { field: 'manufacturer', headerName: 'Manufacturer' },
    { field: 'department_name', headerName: 'Section/Unit' },
    { field: 'location', headerName: 'Location' },
    { field: 'current_stock', headerName: 'In Stock' },
    { field: 'reorder_point', headerName: 'Reorder Point' },
    { field: 'unit_cost', headerName: 'Unit Cost', renderValue: (v) => v ? `UGX ${Number(v).toLocaleString()}` : '' },
    { field: 'expiry_date', headerName: 'Expiry Date', renderValue: (v) => v ? formatDate(v) : '' },
  ], [])

  // Keep refs in sync
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { typeFilterRef.current = typeFilter }, [typeFilter])
  useEffect(() => { searchTermRef.current = searchTerm }, [searchTerm])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const fetchParts = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const currentTypeFilter = typeFilterRef.current
      const params = {
        page: currentPage + 1,
        page_size: currentPageSize,
      }
      if (search) params.search = search
      if (currentTypeFilter) params.inventory_type = currentTypeFilter
      if (currentFilters.category) params.category = currentFilters.category
      if (currentFilters.department) params.department = currentFilters.department
      if (currentFilters.low_stock === 'true') params.is_low_stock = true

      const response = await api.get('/inventory/spare-parts/', { params })
      setParts(response.data.results || response.data)
      setTotalCount(response.data.count || 0)
    } catch (error) {
      console.error('Error fetching inventory:', error)
      toast.error('Failed to load inventory items')
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchParts(newPage, newPageSize, searchTermRef.current)
  }, [fetchParts])

  const handleSearch = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)
    fetchParts(0, pageSizeRef.current, search)
  }, [fetchParts])

  const fetchFilterOptions = async () => {
    try {
      const [catRes, deptRes] = await Promise.all([
        api.get('/inventory/categories/'),
        api.get('/common/departments/'),
      ])
      setCategories(catRes.data.results || catRes.data || [])
      setDepartments(deptRes.data.results || deptRes.data || [])
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchParts(0, pageSizeRef.current, '')
  }, [typeFilter, filters, fetchParts])

  useEffect(() => {
    fetchFilterOptions()
  }, [])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ category: '', department: '', low_stock: '' })
  }

  const hasActiveFilters = filters.category || filters.department || filters.low_stock

  const getActiveFilterLabels = () => {
    const labels = {}
    if (typeFilter) labels.Type = INVENTORY_TYPES.find(t => t.value === typeFilter)?.label || typeFilter
    if (filters.category) {
      const cat = categories.find(c => c.id === parseInt(filters.category))
      labels.Category = cat?.name || filters.category
    }
    if (filters.department) {
      const dept = departments.find(d => d.id === parseInt(filters.department))
      labels['Section/Unit'] = dept?.name || filters.department
    }
    if (filters.low_stock === 'true') labels['Stock Status'] = 'Low Stock Only'
    return labels
  }

  // Calculate stats
  const lowStockCount = parts.filter((p) => p.is_low_stock || p.current_stock <= p.reorder_point).length
  const expiringCount = parts.filter((p) => {
    if (!p.expiry_date) return false
    const daysUntil = Math.ceil((new Date(p.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
    return daysUntil >= 0 && daysUntil <= 30
  }).length

  const handleFormSuccess = () => {
    fetchParts(page, pageSize, searchTerm)
    setSelectedItem(null)
  }

  const handleEdit = (item) => {
    setSelectedItem(item)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    setDeleting(true)
    try {
      await api.delete(`/inventory/spare-parts/${selectedItem.id}/`)
      toast.success('Item deleted successfully')
      fetchParts(page, pageSize, searchTerm)
      setDeleteModalOpen(false)
      setSelectedItem(null)
    } catch (error) {
      console.error('Error deleting item:', error)
      toast.error('Failed to delete item')
    } finally {
      setDeleting(false)
    }
  }

  const actionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        {canEdit('inventory') && (
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          >
            <Edit fontSize="small" />
          </Button>
        )}
        {canDelete('inventory') && (
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              setSelectedItem(row)
              setDeleteModalOpen(true)
            }}
          >
            <Delete fontSize="small" />
          </Button>
        )}
      </Box>
    ),
  }

  return (
    <Box>
      <PageHeader
        title="Stock"
        subtitle="Manage spare parts, reagents, and supplies"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Stock' },
        ]}
        actions={
          <>
            <ExportButtons
              data={parts}
              columns={exportColumns}
              title="Stock List"
              filename="stock"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length})`}
            </Button>
            <PermissionGate permission="inventory.create">
              <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
                Add Item
              </Button>
            </PermissionGate>
          </>
        }
      />

      {lowStockCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          {lowStockCount} item(s) are low on stock and need to be reordered
        </Alert>
      )}

      {expiringCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {expiringCount} item(s) expiring within 30 days
        </Alert>
      )}

      {/* Additional Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={handleFilterChange('category')}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Section/Unit</InputLabel>
              <Select
                value={filters.department}
                onChange={handleFilterChange('department')}
                label="Section/Unit"
              >
                <MenuItem value="">All Sections/Units</MenuItem>
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Stock Status</InputLabel>
              <Select
                value={filters.low_stock}
                onChange={handleFilterChange('low_stock')}
                label="Stock Status"
              >
                <MenuItem value="">All Items</MenuItem>
                <MenuItem value="true">Low Stock Only</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).filter(([key]) => key !== 'Type').map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={() => {
                    const fieldMap = { 'Category': 'category', 'Section/Unit': 'department', 'Stock Status': 'low_stock' }
                    setFilters(prev => ({ ...prev, [fieldMap[key]]: '' }))
                  }}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <Tabs
          value={typeFilter}
          onChange={(_, v) => setTypeFilter(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {INVENTORY_TYPES.map((type) => (
            <Tab
              key={type.value}
              value={type.value}
              label={type.label}
              icon={type.icon}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        <DataTable
          columns={[...columns, actionsColumn]}
          data={parts}
          loading={loading}
          searchPlaceholder="Search inventory..."
          emptyMessage="No items found"
          serverSide
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={handlePaginationChange}
          onSearch={handleSearch}
        />
      </Card>

      <InventoryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setSelectedItem(null)
        }}
        item={selectedItem}
        onSuccess={handleFormSuccess}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Spare Part"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{selectedItem?.name}"?
      </Modal>
    </Box>
  )
}

export default InventoryList
